import type {
  AssetTable,
  EdgeTreatment,
  HeightmapAsset,
  PanelDesign,
  PanelRef,
  PatternLayer,
  PatternSample,
  ReliefFinish,
  Vec2,
  WallMetrics,
} from './types';
import { DEG2RAD, clamp01, contrastCurve, gammaCurve, quantize, smoothstep } from './math/scalar';
import { resolveParams } from './params';
import { requirePattern, type AnyPatternDefinition } from './patterns/registry';
import { blendHeight } from './blend';
import type { WallLayout } from './wall';

/**
 * The height field: the single place where a design becomes a number.
 *
 * Everything — the live preview, the pattern thumbnails, the exported mesh —
 * asks this one evaluator, so what you see really is what gets printed.
 *
 * Coordinates arrive in wall space, which is the mechanism behind seamless
 * multi-panel motifs: the evaluator never learns which panel it is working on
 * except through `PanelRef`, so two points on either side of a joint that are
 * the same distance apart as any other two points produce the same relief.
 */

interface CompiledLayer {
  definition: AnyPatternDefinition;
  params: Record<string, number | string | boolean>;
  prepared: unknown;
  layer: PatternLayer;
  /** Precomputed transform terms, so the hot path does no trigonometry. */
  transform: {
    active: boolean;
    cos: number;
    sin: number;
    invScale: number;
    offsetX: number;
    offsetY: number;
    mirrorX: number;
    mirrorY: number;
  };
}

export interface HeightField {
  wall: WallMetrics;
  /** Normalised height `0 .. 1` at a wall-space point inside `panel`. */
  sample(x: number, y: number, panel: PanelRef): number;
  /** Height in millimetres above the backing plate. */
  sampleMillimetres(x: number, y: number, panel: PanelRef): number;
  /** Number of layers that actually contribute. */
  activeLayerCount: number;
}

export interface HeightFieldOptions {
  layers: readonly PatternLayer[];
  finish: ReliefFinish;
  wall: WallMetrics;
  footprint: { minX: number; minY: number; maxX: number; maxY: number };
  assets?: AssetTable;
  /** Relief depth in millimetres, used by `sampleMillimetres`. */
  reliefDepth?: number;
  /** Panels used for the auto-level pre-pass. */
  panels?: readonly PanelRef[];
}

function compileLayer(layer: PatternLayer, wall: WallMetrics, assets: AssetTable): CompiledLayer {
  const definition = requirePattern(layer.patternId);
  const params = resolveParams(definition.params, layer.params);
  const prepared = definition.prepare ? definition.prepare(params, { wall, assets }) : undefined;

  const t = layer.transform;
  const angle = -t.rotation * DEG2RAD;
  const scale = Math.abs(t.scale) < 1e-4 ? 1 : t.scale;

  return {
    definition,
    params,
    prepared,
    layer,
    transform: {
      active:
        t.rotation !== 0 ||
        scale !== 1 ||
        t.offsetX !== 0 ||
        t.offsetY !== 0 ||
        t.mirrorX ||
        t.mirrorY,
      cos: Math.cos(angle),
      sin: Math.sin(angle),
      invScale: 1 / scale,
      offsetX: t.offsetX,
      offsetY: t.offsetY,
      mirrorX: t.mirrorX ? -1 : 1,
      mirrorY: t.mirrorY ? -1 : 1,
    },
  };
}

export function createHeightField(options: HeightFieldOptions): HeightField {
  const { wall, footprint, finish } = options;
  const assets = options.assets ?? {};
  const reliefDepth = options.reliefDepth ?? 1;

  const compiled = options.layers
    .filter((layer) => layer.enabled && layer.amount > 0)
    .map((layer) => compileLayer(layer, wall, assets));

  const footprintWidth = Math.max(footprint.maxX - footprint.minX, 1e-6);
  const footprintHeight = Math.max(footprint.maxY - footprint.minY, 1e-6);
  const centreX = wall.width * 0.5;
  const centreY = wall.height * 0.5;

  // Reused between calls: `sample` runs once per mesh vertex, and a wall can
  // easily reach a few million vertices, so it must not allocate.
  const scratch: PatternSample = {
    x: 0,
    y: 0,
    u: 0,
    v: 0,
    panel: { column: 0, row: 0, centerX: 0, centerY: 0, size: wall.panelSize, rotation: 0 },
    wall,
  };

  function rawSample(x: number, y: number, panel: PanelRef): number {
    let field = 0;

    // Panel-local coordinates always describe the real panel, never a
    // transformed one: a layer transform moves the motif, not the panel.
    let localX = x - panel.centerX;
    let localY = y - panel.centerY;

    // A rotated panel (a down-pointing triangle) has its own frame, so undo the
    // placement rotation before measuring where in the panel this point falls.
    // Wall coordinates are deliberately left alone: that is what keeps a motif
    // continuous across the joint regardless of how the panel is turned.
    if (panel.rotation !== 0) {
      const angle = (-panel.rotation * Math.PI) / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const rx = localX * cos - localY * sin;
      localY = localX * sin + localY * cos;
      localX = rx;
    }

    const u = clamp01((localX - footprint.minX) / footprintWidth);
    const v = clamp01((localY - footprint.minY) / footprintHeight);

    for (let i = 0; i < compiled.length; i += 1) {
      const entry = compiled[i];
      let sx = x;
      let sy = y;

      if (entry.transform.active) {
        const t = entry.transform;
        let px = (x - centreX - t.offsetX) * t.mirrorX;
        let py = (y - centreY - t.offsetY) * t.mirrorY;
        const rx = px * t.cos + py * t.sin;
        const ry = -px * t.sin + py * t.cos;
        px = rx * t.invScale;
        py = ry * t.invScale;
        sx = px + centreX;
        sy = py + centreY;
      }

      scratch.x = sx;
      scratch.y = sy;
      scratch.u = u;
      scratch.v = v;
      scratch.panel = panel;

      const height = entry.definition.sample(scratch, entry.params, entry.prepared);

      // The bottom layer composites onto an empty field, so its blend mode has
      // nothing to blend against and is treated as `replace`.
      field =
        i === 0
          ? clamp01(height) * clamp01(entry.layer.amount)
          : blendHeight(field, height, entry.layer.blend, entry.layer.amount);
    }

    return field;
  }

  // Auto-level needs to know the range the design actually occupies, which is
  // only knowable by looking. A coarse pre-pass is enough: it is used to
  // rescale, not to clip, so a slightly loose range costs a little depth and
  // never clips a peak.
  let levelOffset = 0;
  let levelScale = 1;
  if (finish.autoLevel && options.panels && options.panels.length > 0 && compiled.length > 0) {
    const { min, max } = measureRange(rawSample, options.panels, footprint);
    if (max - min > 1e-4) {
      levelOffset = min;
      levelScale = 1 / (max - min);
    }
  }

  function finished(value: number): number {
    let height = clamp01((value - levelOffset) * levelScale);
    if (finish.invert) height = 1 - height;
    height = gammaCurve(height, finish.gamma);
    height = contrastCurve(height, finish.contrast);
    height = quantize(height, finish.terraceSteps);
    return clamp01(height);
  }

  return {
    wall,
    activeLayerCount: compiled.length,
    sample(x, y, panel) {
      return finished(rawSample(x, y, panel));
    },
    sampleMillimetres(x, y, panel) {
      return finished(rawSample(x, y, panel)) * reliefDepth;
    },
  };
}

/** Coarse min/max probe used by auto-level. */
function measureRange(
  sampler: (x: number, y: number, panel: PanelRef) => number,
  panels: readonly PanelRef[],
  footprint: { minX: number; minY: number; maxX: number; maxY: number },
): { min: number; max: number } {
  const steps = 12;
  let min = Infinity;
  let max = -Infinity;

  for (const panel of panels) {
    const angle = (panel.rotation * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    for (let j = 0; j <= steps; j += 1) {
      for (let i = 0; i <= steps; i += 1) {
        const lx = footprint.minX + ((footprint.maxX - footprint.minX) * i) / steps;
        const ly = footprint.minY + ((footprint.maxY - footprint.minY) * j) / steps;
        const value = sampler(
          panel.centerX + lx * cos - ly * sin,
          panel.centerY + lx * sin + ly * cos,
          panel,
        );
        if (value < min) min = value;
        if (value > max) max = value;
      }
    }
  }

  return { min: Number.isFinite(min) ? min : 0, max: Number.isFinite(max) ? max : 1 };
}

/**
 * Multiplier that shapes the relief near the panel border.
 *
 * `seamless` leaves the relief untouched so neighbouring panels continue the
 * motif. The other modes trade that continuity for a defined panel edge:
 * `flat-border` gives every panel a flat frame to glue and butt together, and
 * `chamfer` ramps the relief down so joints read as a deliberate V-groove.
 */
export function edgeFalloff(
  localX: number,
  localY: number,
  outline: readonly Vec2[],
  treatment: EdgeTreatment,
): number {
  if (treatment.mode === 'seamless' || treatment.width <= 0) return 1;

  const distance = distanceToOutline(localX, localY, outline);

  if (treatment.mode === 'chamfer') {
    return smoothstep(0, treatment.width, distance);
  }

  const ramp = Math.min(treatment.width, 3);
  return smoothstep(treatment.width, treatment.width + ramp, distance);
}

/**
 * Inward distance from a point to a convex outline. Points outside the outline
 * return `0`, which is the behaviour both edge modes want at the border.
 */
export function distanceToOutline(x: number, y: number, outline: readonly Vec2[]): number {
  let distance = Infinity;
  for (let i = 0; i < outline.length; i += 1) {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const length = Math.hypot(ex, ey);
    if (length < 1e-9) continue;
    // Positive on the inward side because the outline is counter-clockwise.
    const signed = ((x - a.x) * ey - (y - a.y) * ex) / length;
    if (-signed < distance) distance = -signed;
  }
  return Math.max(0, distance);
}

/** Builds the runtime asset table from a design's embedded bitmaps. */
export function assetTable(assets: readonly HeightmapAsset[]): AssetTable {
  const table: Record<string, HeightmapAsset> = {};
  for (const asset of assets) table[asset.id] = asset;
  return table;
}

export function createHeightFieldForDesign(design: PanelDesign, layout: WallLayout): HeightField {
  return createHeightField({
    layers: design.source.layers,
    finish: design.finish,
    wall: layout.metrics,
    footprint: layout.footprint,
    assets: assetTable(design.assets),
    reliefDepth: design.panel.reliefDepth,
    panels: layout.panels,
  });
}
